import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { SafeDialog } from "components/Dialog";
import {
  AddEditDocumentDialog,
  AddEditDocumentDialogState,
  defaultAddEditDocumentDialogState,
  EditMode,
  FormState,
} from "components/Document";
import { DocumentType } from "generated-graphql/graphql";
import { useSwapSubmissionDocument } from "hooks/useSubmissionMutations";
import { useCallback, useMemo, useState } from "react";

type UploadedDocument = {
  id: string;
  title: string;
  documentType?: DocumentType;
};

interface SwapSubmissionDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  submissionId: string;
  submissionDocumentId: string;
  tenantId: string;
  currentDocumentName: string;
  currentDocumentType?: DocumentType;
  signatureWillBeInvalidated: boolean;
  onSuccess?: () => Promise<void> | void;
}

export function SwapSubmissionDocumentDialog({
  open,
  onClose,
  submissionId,
  submissionDocumentId,
  tenantId,
  currentDocumentName,
  currentDocumentType,
  signatureWillBeInvalidated,
  onSuccess,
}: SwapSubmissionDocumentDialogProps) {
  const alerts = useAlerts();
  const [verified, setVerified] = useState(false);
  const [uploadedDocument, setUploadedDocument] = useState<UploadedDocument | null>(null);
  const [uploadDialogState, setUploadDialogState] = useState<AddEditDocumentDialogState>(
    defaultAddEditDocumentDialogState
  );
  const [swapSubmissionDocument, { loading }] = useSwapSubmissionDocument();
  const defaultDocumentType = useMemo(
    () => currentDocumentType ?? DocumentType.Other,
    [currentDocumentType]
  );

  const resetAndClose = useCallback(() => {
    setVerified(false);
    setUploadedDocument(null);
    setUploadDialogState(defaultAddEditDocumentDialogState);
    onClose();
  }, [onClose]);

  const handleClose = useCallback(() => {
    if (loading) {
      return;
    }
    resetAndClose();
  }, [loading, resetAndClose]);

  const handleOpenUploadDialog = useCallback(() => {
    setUploadDialogState({
      ...defaultAddEditDocumentDialogState,
      open: true,
      mode: EditMode.ADD,
      step: FormState.UPLOAD,
      documentType: defaultDocumentType,
    });
  }, [defaultDocumentType]);

  const handleSwap = useCallback(async () => {
    if (!uploadedDocument?.id) {
      alerts.error("Please upload a replacement document before saving");
      return;
    }

    try {
      await swapSubmissionDocument({
        variables: {
          input: {
            submissionId,
            submissionDocumentId,
            documentId: uploadedDocument.id,
          },
        },
      });

      alerts.success("Submission document swapped successfully");
      await onSuccess?.();
      resetAndClose();
    } catch (error) {
      alerts.error("Failed to swap submission document", error as Error);
    }
  }, [
    alerts,
    onSuccess,
    resetAndClose,
    submissionDocumentId,
    submissionId,
    swapSubmissionDocument,
    uploadedDocument?.id,
  ]);

  const canSave = Boolean(uploadedDocument?.id && verified && !loading);

  return (
    <>
      <SafeDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Swap Submission Document</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="warning">
              You are about to swap out the current submission document with a new one. Non-signer
              approvals are preserved, but any signer signature is always reset.
            </Alert>

            {signatureWillBeInvalidated ? (
              <Alert severity="error">
                This document has been prepared for signing or already signed. Swapping it
                invalidates the existing signature. To finish this submission you will need to
                either (a) re-prepare the new document for signing and have the signer re-sign, or
                (b) change the signature requirement (e.g. no signature or wet-ink) so it can be
                completed without re-signing.
              </Alert>
            ) : null}

            <Box>
              <Typography variant="body2" color="text.secondary">
                Current document: {currentDocumentName}
              </Typography>
            </Box>

            <Box>
              <Button variant="outlined" onClick={handleOpenUploadDialog} disabled={loading}>
                Upload replacement document
              </Button>
            </Box>

            {uploadedDocument ? (
              <Typography variant="body2" color="text.secondary">
                Replacement document: {uploadedDocument.title}
              </Typography>
            ) : null}

            <FormControlLabel
              control={
                <Checkbox
                  checked={verified}
                  onChange={(event) => setVerified(event.target.checked)}
                  disabled={loading}
                />
              }
              label={
                signatureWillBeInvalidated
                  ? "I understand the existing signature will be invalidated, and that to finish this submission I will need to either re-prepare the new document for signing and have the signer re-sign, or change the signature requirement so it can be completed without re-signing."
                  : "I verify that this submission document needs to be swapped out."
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSwap}
            disabled={!canSave}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </SafeDialog>

      {uploadDialogState.open && (
        <AddEditDocumentDialog
          dialogState={uploadDialogState}
          setDialogState={setUploadDialogState}
          tenantId={tenantId}
          showFacilityPicker={false}
          refetchQueries={[]}
          onSubmit={(document) => {
            setUploadedDocument({
              id: document.id,
              title: document.title,
              documentType: document.documentType,
            });
          }}
        />
      )}
    </>
  );
}
